import { Field, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import debounce from 'lodash/debounce';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import * as Yup from 'yup';
import { formikAlert } from "../components/Alerts/formikAlert";
import { EVARIANT, MessageBanner } from "../components/Alerts/MessageBanner";
import { showAlert } from "../components/Alerts/Popup";
import { toast } from "../components/Alerts/Toast";
import { useErrorAlerts } from "../components/Alerts/useErrorAlerts";
import { IAthlete, IClub, athleteDB, clubDB, getUserData, modifyUser } from '../db';
import { inviteManagerEmail } from '../db/Clients/request';
import { nameExp } from '../utils/regex';

export enum EMANAGER_ROLES {
    CLUB_ADMIN = 'admin',
    EVENT_MANAGER = 'manager',
}

export const useAddManager = (fetchClubs: Function) => {
    const { t } = useTranslation();
    const handleError = useErrorAlerts();

    const Schema = Yup.object({
        email: Yup.string().required(t('EMAIL_REQUIRED')).email(t('EMAIL_INVALID_FORMAT')).default(''),
        name: Yup.string().max(80, t('NAME_LONG_ERROR')).required(t('NAME_REQUIRED')).matches(nameExp, t('NAME_NOT_VALID')).default(''),
        role: Yup.string().oneOf([EMANAGER_ROLES.CLUB_ADMIN, EMANAGER_ROLES.EVENT_MANAGER]).required().default(EMANAGER_ROLES.EVENT_MANAGER),
        status: Yup.string().oneOf(['accepted', 'pending']).required().default('pending'),
        id: Yup.string().default(''),
    });

    type FormValues = Yup.InferType<typeof Schema>;

    let formikRef: FormikProps<FormValues> | null = null;

    const handleSubmit = async (values: FormValues, actions: FormikHelpers<FormValues>, clubId: string) => {
        // Email will be sent to only users who are not currently a member.
        // Existing users will be auto approved.
        // Show warning and cancel operation if already a owner
        try {
            const club = await clubDB().get(clubId);

            if (club._rev) {
                // Check if already a owner
                if (club.owners?.some((owner) => owner.id === values.id)) {
                    showAlert({
                        icon: 'error',
                        text: t('EXISTING_MANAGER'),
                        confirmButtonText: t('OK'),
                        footer: `<a href="https://docs.targetscore.com/docs/contact" target="_blank">${t("REPORT_AN_ISSUE")}</a>`,
                    });
                    return; // Cancel operation
                }
                // Add Owner
                const result = await clubDB().put({
                    ...club,
                    owners: [...club.owners!, { ...values }],
                } as any);
                if (result.ok && values.id) {
                    // If existing, modify the user permission
                    const userData = await getUserData(values.email);
                    if (!userData.error) {
                        modifyUser({
                            name: userData.name,
                            roles: [...new Set([...userData.roles!, ...[values.role === 'admin' ? 'club' : 'manager']])],
                            metadata: {
                                ...userData.metadata!,
                                clubs: [...(userData.metadata!.clubs || []), clubId],
                            },
                        })
                            .then((res) => {
                                inviteManagerEmail({
                                    club: clubId,
                                    email: values.email,
                                    name: values.name,
                                    role: values.role,
                                    status: 'AUTO',
                                });
                                fetchClubs();
                                toast({ title: t('MANAGER_ADDED'), timer: 2000 });
                            })
                            .catch((e) => handleError(e, 'add_manager_modify_user'));
                    }
                } else {
                    // value.id is undefined means not an existing user
                    // Send Invite
                    inviteManagerEmail({
                        club: clubId,
                        email: values.email,
                        name: values.name,
                        role: values.role,
                    })
                        .then((res) => {
                            fetchClubs();
                            toast({ title: t('MANAGER_ADDED'), timer: 2000 });
                        })
                        .catch((e) => {
                            showAlert({
                                icon: 'error',
                                text: t('FAILED_TO_SEND_INVITE'),
                                confirmButtonText: t('OK'),
                                footer: `<a href="https://docs.targetscore.com/docs/contact" target="_blank">${t("REPORT_AN_ISSUE")}</a>`,
                            });
                            handleError(e, 'add_manager_invite');
                        });
                }
            }
        } catch (e) {
            handleError(e, 'add_manager');
        }
    };

    return (club: IClub) =>
        formikAlert(
            <Formik
                innerRef={(ref) => (formikRef = ref)}
                initialValues={{
                    email: '',
                    name: '',
                    role: 'manager',
                    status: 'pending',
                    id: '',
                }}
                validationSchema={Schema}
                onSubmit={(values, actions) => {
                    handleSubmit(values, actions, club._id);
                }}
            >
                {({ errors, submitCount, touched, values, handleSubmit, setFieldValue, setFieldTouched }) => {
                    const [athlete, setAthlete] = useState<IAthlete | undefined>();

                    const fetchExistingUser = async (email: string) => {
                        const fetchAthletes = async () => {
                            // athleteDB().createIndex({
                            //     index: { fields: ['email', 'doc_type'] },
                            // });
                            const athlete = await athleteDB().find({
                                selector: {
                                    doc_type: { $eq: 'athlete' },
                                    email: email,
                                },
                            });
                            setAthlete(athlete.docs[0] || null);
                        };
                        const debounced = debounce(fetchAthletes, 500, { maxWait: 1000 });
                        debounced();
                    };

                    useEffect(() => {
                        if (values.email.length > 3 && !errors.email) {
                            fetchExistingUser(values.email);
                        }
                    }, [values.email, errors.email]);

                    useEffect(() => {
                        if (athlete) {
                            setFieldValue('id', athlete._id);
                            setFieldValue('name', athlete.fullname);
                            setFieldValue('status', 'accepted');
                        } else {
                            setFieldValue('id', '');
                            setFieldValue('name', '');
                            setFieldValue('status', 'pending');
                        }
                    }, [athlete]);

                    return (
                        <Form className="space-y-5">
                            <input
                                type="text"
                                className="form-input"
                                onChange={(e) => {
                                    const value = e.target.value || '';
                                    setFieldValue('email', value.toLowerCase());
                                    setFieldTouched('email');
                                }}
                                value={values.email}
                                name="email"
                                placeholder="Enter email address"
                            />
                            <Field type="text" className="form-input disabled:bg-gray-100" name="name" placeholder="Enter name" disabled={Boolean(athlete)} readOnly={Boolean(athlete)} />
                            <div className="flex gap-x-4 gap-y-1 ms-2 flex-wrap" role="group" aria-labelledby="genderGroup">
                                <label className="inline-flex">
                                    <Field type="radio" name="role" className="form-radio text-secondary" value={EMANAGER_ROLES.EVENT_MANAGER} />
                                    <span>{t("EVENT_MANAGER")}</span>
                                </label>
                                <label className="inline-flex">
                                    <Field type="radio" name="role" className="form-radio text-secondary" value={EMANAGER_ROLES.CLUB_ADMIN} />
                                    <span>{t("CLUB_ADMIN")}</span>
                                </label>
                            </div>
                            <div className="inline-flex">
                                {(athlete || athlete === null) && values.email && (
                                    <MessageBanner variant={athlete ? EVARIANT.SUCCESS : EVARIANT.WARNING}>{t(values.status === 'pending' ? 'INVITE_WILL_SEND' : 'EXISTING_FOUND')}</MessageBanner>
                                )}
                            </div>
                        </Form>
                    );
                }}
            </Formik>,
            async () => {
                await formikRef?.submitForm();
                if (formikRef?.isValid) {
                    Swal.fire({
                        title: t("MANAGER_ADDED"),
                        icon: 'success',
                    });
                } else {
                    Swal.showValidationMessage(JSON.stringify(formikRef?.errors));
                }
            }
        );
};
